/**
 * BecomeHostCTA Component
 *
 * Call-to-action section for property owners to become hosts.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Container } from '../../../components/layout';

export const BecomeHostCTA: React.FC = () => {
  return (
    <section className="py-20 bg-gray-900 text-white">
      <Container>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Rent your place. Pay your bills.
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              150+ Lebanese owners list their second home, weekend chalet, or
              spare apartment on DARAK. You set the price and the calendar.
            </p>

            {/* Benefits List */}
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <CheckIcon className="w-6 h-6 text-primary-400 shrink-0 mt-0.5" />
                <span>
                  <strong className="text-white">Block dates whenever</strong>
                  <span className="text-gray-400 block text-sm">
                    Your weekend stays your weekend
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckIcon className="w-6 h-6 text-primary-400 shrink-0 mt-0.5" />
                <span>
                  <strong className="text-white">Price the way you want</strong>
                  <span className="text-gray-400 block text-sm">
                    Charge more on long weekends, less in winter
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckIcon className="w-6 h-6 text-primary-400 shrink-0 mt-0.5" />
                <span>
                  <strong className="text-white">Paid the day after check-in</strong>
                  <span className="text-gray-400 block text-sm">
                    Whish or bank transfer, your call
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckIcon className="w-6 h-6 text-primary-400 shrink-0 mt-0.5" />
                <span>
                  <strong className="text-white">A human on the other end</strong>
                  <span className="text-gray-400 block text-sm">
                    Call the Beirut team if a guest causes trouble
                  </span>
                </span>
              </li>
            </ul>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Link
                to="/become-host"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-xl hover:bg-primary-600 transition-colors"
              >
                Start Hosting
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/30 text-white font-medium rounded-xl hover:bg-white/10 transition-colors"
              >
                Learn More
              </Link>
            </div>
          </div>

          {/* Image/Stats */}
          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80"
                alt="Beautiful Lebanese property"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Floating Stats Card */}
            <div className="absolute -bottom-6 -left-6 bg-white text-gray-900 rounded-2xl p-6 shadow-xl max-w-[240px]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <MoneyIcon className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">$2,500</p>
                  <p className="text-sm text-gray-500">avg. monthly earning</p>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                Two-bedroom in Batroun, summer pricing
              </p>
            </div>

            {/* Floating Reviews Card */}
            <div className="absolute -top-4 -right-4 bg-white text-gray-900 rounded-xl p-4 shadow-lg">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white" />
                  <div className="w-8 h-8 rounded-full bg-gray-300 border-2 border-white" />
                  <div className="w-8 h-8 rounded-full bg-gray-400 border-2 border-white" />
                </div>
                <div>
                  <p className="font-semibold text-sm">150+ hosts</p>
                  <p className="text-xs text-gray-500">on DARAK</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

// Icons
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const ArrowRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 8l4 4m0 0l-4 4m4-4H3"
    />
  </svg>
);

const MoneyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export default BecomeHostCTA;
